<template>
  <v-card
    flat
    class="vetalfa-card-grey-border pa-0 timeline-card"
    style="border-left: 4px solid #9c27b0; overflow: hidden"
  >
    <v-row class="ma-0 pa-0" align="center" no-gutters>
      <v-col cols="auto" class="pa-3 d-flex align-center vetalfa-pointer" @click="open">
        <v-avatar size="36" color="purple" class="white--text">
          <v-icon small color="white">{{ mdiTestTube }}</v-icon>
        </v-avatar>
      </v-col>
      <v-col class="pa-3 pl-0 vetalfa-pointer" @click="open">
        <p class="ma-0 subtitle-2 font-weight-bold">
          {{ labExam.name || _getLabel("LAB_EXAM") }}
        </p>
        <p class="ma-0 caption grey--text text--darken-1">
          {{ formattedDate }}
        </p>
        <div class="mt-1">
          <v-chip
            x-small
            :color="isFinished ? 'success' : 'grey lighten-2'"
            :text-color="isFinished ? 'white' : 'grey darken-2'"
          >
            {{ isFinished ? _getLabel("FINALIZED") : _getLabel("DRAFT") }}
          </v-chip>
          <v-chip
            v-if="hasAddenda"
            x-small
            class="ml-1"
            color="blue lighten-4"
            text-color="blue darken-2"
          >
            {{ _getLabel("WITH_ADDENDUM") }}
          </v-chip>
          <v-chip
            x-small
            class="ml-1"
            color="grey lighten-2"
            text-color="grey darken-2"
          >
            {{ typeLabel }}
          </v-chip>
          <v-chip
            v-if="files.length"
            x-small
            class="ml-1"
            color="blue lighten-4"
            text-color="blue darken-2"
          >
            {{ files.length }}
            {{
              files.length === 1
                ? _getLabel("FILE_SINGULAR")
                : _getLabel("FILE_PLURAL")
            }}
          </v-chip>
        </div>
        <p v-if="isFinished" class="ma-0 caption grey--text text--darken-1 mt-1">
          {{ _getLabel("FINALIZED_BY") }}: {{ signatureText }}
        </p>
      </v-col>
      <v-col cols="auto" class="pr-2 vetalfa-pointer" @click="open">
        <v-icon x-small color="grey">{{ mdiChevronRight }}</v-icon>
      </v-col>
    </v-row>

    <v-divider></v-divider>
    <v-row no-gutters class="px-3 pt-2">
      <v-col cols="12">
        <p class="ma-0 caption font-weight-bold grey--text text--darken-2">
          {{ _getLabel("ATTACHED_FILES") }}
        </p>
        <p v-if="!files.length" class="ma-0 caption grey--text text--darken-1">
          {{ _getLabel("NO_ATTACHED_FILES") }}
        </p>
        <v-row
          v-for="(file, index) in files"
          :key="`file-${index}`"
          no-gutters
          align="center"
          class="mt-1"
        >
          <v-col class="text-truncate">
            <span class="caption">{{ file.name || _getLabel("FILE_SINGULAR") }}</span>
          </v-col>
          <v-col cols="auto">
            <v-btn
              x-small
              text
              color="primary"
              class="text-none"
              :disabled="!file.url"
              @click.stop="$emit('openFile', file)"
            >
              {{ _getLabel("OPEN_FILE") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row no-gutters class="px-2 py-1" align="center">
      <v-btn
        v-if="!isFinished"
        x-small
        text
        color="primary"
        class="text-none"
        @click.stop="pickFile"
      >
        <v-icon x-small left>{{ mdiPaperclip }}</v-icon>
        {{ _getLabel("ATTACH_REPORT") }}
      </v-btn>
      <v-btn
        v-if="!isFinished"
        x-small
        text
        color="primary"
        class="text-none"
        @click.stop="$emit('finalize', labExam)"
      >
        <v-icon x-small left>{{ mdiLockOutline }}</v-icon>
        {{ _getLabel("FINALIZE") }}
      </v-btn>
      <v-btn
        v-if="!isFinished"
        x-small
        text
        color="error"
        class="text-none"
        @click.stop="$emit('remove', labExam)"
      >
        <v-icon x-small left>{{ mdiTrashCanOutline }}</v-icon>
        {{ _getLabel("DELETE_RECORD") }}
      </v-btn>
    </v-row>

    <input
      ref="fileInput"
      type="file"
      class="d-none"
      accept="application/pdf,image/jpeg,image/png"
      @change="onFileSelected"
    />
  </v-card>
</template>

<script>
import {
  mdiChevronRight,
  mdiTestTube,
  mdiPaperclip,
  mdiLockOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import labExamsEnum from "@/constants/lab-exams.enum.js";

export default {
  name: "LabExamCard",
  props: {
    labExam: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mdiChevronRight,
      mdiTestTube,
      mdiPaperclip,
      mdiLockOutline,
      mdiTrashCanOutline,
    };
  },
  computed: {
    formattedDate() {
      if (!this.labExam.requestDate && !this.labExam.lastUpdate) return "-";
      return this._getDateFromTimestamp(
        this.labExam.requestDate || this.labExam.lastUpdate
      );
    },
    typeLabel() {
      return labExamsEnum.getTypeLabel(this.labExam.type, this._getLabel);
    },
    isFinished() {
      return !!this.labExam.finalizedAt;
    },
    hasAddenda() {
      return ((this.labExam.addenda || []).length || 0) > 0;
    },
    /**
     * `fileUrls` chega como lista de objetos com a URL assinada de 15 minutos.
     * O formato antigo (string com a URL permanente) continua sendo exibido.
     */
    files() {
      return (this.labExam.fileUrls || []).map((file) =>
        typeof file === "string" ? { name: "", url: file } : file || {}
      );
    },
    signatureText() {
      const author = this.labExam.finalizedBy || {};
      const name = [author.displayNamePrefix, author.displayName]
        .filter((part) => !!part)
        .join(" ");
      const crmv =
        author.crmvNumber || author.crmvState
          ? `CRMV ${author.crmvNumber || ""}${
              author.crmvState ? "-" + author.crmvState : ""
            }`.trim()
          : "";
      const date = this.labExam.finalizedAt
        ? new Date(this.labExam.finalizedAt).toLocaleString("pt-BR")
        : "";
      return [name, crmv, date].filter((part) => !!part).join(" • ") || "-";
    },
  },
  methods: {
    open() {
      this.$emit("open", this.labExam);
    },
    pickFile() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const input = event.target;
      const file = input && input.files && input.files.length ? input.files[0] : null;
      // O valor e limpo para permitir anexar o mesmo arquivo novamente apos um erro.
      if (input) input.value = "";
      if (!file) return;
      this.$emit("attachFile", { labExam: this.labExam, file });
    },
  },
};
</script>

<style scoped>
.timeline-card {
  transition: box-shadow 0.2s;
}
.timeline-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
</style>
